import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import type { ReservationReportRow } from '../api/types/reports';

/**
 * Renders a reservation-list row's PNR (falling back to its ticket/document number when
 * no PNR is present) as a link into `ReservationDetailPage` — replaces the old inline
 * Detay/Void/İptal Et row-action buttons across every reservation list (see
 * modules/air/components/AirReservationList.tsx, modules/air/pages/AirEntryFindPage.tsx,
 * the Hotel/Bus/Transfer/RentCar `*ReservationsPage.tsx` files, and the Dashboard's
 * recent-activity widgets). `detailBasePath` must be an absolute path (e.g.
 * `/air/reservations`) matching the module's own `:identifier` detail route.
 *
 * `field` picks which identifier to prefer/display — defaults to `'pnr'` (used by every
 * "Rezervasyon Listesi"/"Kayıt Arama" list, where a PNR is the natural row identity).
 * Pass `field="ticket"` for ticket-oriented lists ("Bilet Listesi", the Dashboard's "Son
 * Biletler" card) where the ticket/document number is what the admin actually wants to
 * see, not the PNR. Either way this falls back to the other field when the preferred one
 * is missing on a given row, so a row is never rendered as a dead "—" just because one of
 * the two happens to be empty.
 */
export function ReservationIdentifierLink({
  row,
  detailBasePath,
  field = 'pnr',
}: {
  row: ReservationReportRow;
  detailBasePath: string;
  field?: 'pnr' | 'ticket';
}) {
  const preferred = field === 'ticket' ? row.documentNumber : row.pnr;
  const fallback = field === 'ticket' ? row.pnr : row.documentNumber;
  const identifier = preferred || fallback;
  if (!identifier) return <>—</>;
  const by = identifier === row.pnr ? 'pnr' : 'ticket';
  return (
    <Link component={RouterLink} to={`${detailBasePath}/${encodeURIComponent(identifier)}?by=${by}`} underline="hover">
      {identifier}
    </Link>
  );
}
